"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import IconCrossLarge from "@ui/central-icons/icons/IconCrossLarge";
import IconVideoClip from "@ui/central-icons/icons/IconVideoClip";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import { Button } from "@ui/components/core/Button";
import { LinkTracked } from "@ui/components/core/LinkTracked";
import { PromptIsland, type FileAttachment } from "@ui/components/core/PromptIsland";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@ui/components/core/Tooltip";
import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { publicUrls } from "@/constants";
import { USER_INPUT_PLACEHOLDER } from "../constants";
import {
  isProcessingStep,
  isResultStep,
  type ContentWorkflowStep,
  type GenerationModel,
  type WorkflowInputConfig,
} from "./types";
import { ModelBadge } from "./ModelBadge";

function fileToAttachment(
  file: File,
  previewUrl: string | null,
  opts?: { isVideo?: boolean; statusLabel?: string },
): FileAttachment {
  return {
    name: file.name,
    size: file.size,
    previewUrl: previewUrl ?? "",
    isVideo: opts?.isVideo,
    statusLabel: opts?.statusLabel,
  };
}

interface HeroOmniboxCardProps {
  config: WorkflowInputConfig;
  step: ContentWorkflowStep;
  error: string | null;
  generationPrompt: string | null;
  userInput: string | null;
  generationModel: GenerationModel;
  modelDisplayName: string;
  selectedFile: File | null;
  inputPreviewUrl: string | null;
  redirectCtaUrl: string | null;
  templateCategory: string;
  variantSlug: string;
  onFileSelected: (file: File) => void;
  onTextSubmit: (text: string) => void;
  onSubmit: (userInput?: string) => void;
  onRemoveFile: () => void;
  onRestart: () => void;
  onSignUpClick: () => void;
}

export function HeroOmniboxCard({
  config,
  step,
  error,
  generationPrompt,
  userInput,
  generationModel,
  modelDisplayName,
  selectedFile,
  inputPreviewUrl,
  redirectCtaUrl,
  templateCategory,
  variantSlug,
  onFileSelected,
  onTextSubmit,
  onSubmit,
  onRemoveFile,
  onRestart,
  onSignUpClick,
}: HeroOmniboxCardProps) {
  const t = useTranslations("ClientKeys.creative_templates");
  const isProcessing = isProcessingStep(step);
  const isResult = isResultStep(step);

  if (step === "rate_limited" || step === "quota_exceeded") {
    return (
      <RateLimitedCard
        redirectCtaUrl={redirectCtaUrl}
        onRestart={onRestart}
        isVideo={config.resultType === "video"}
        selectedFile={selectedFile}
        inputPreviewUrl={inputPreviewUrl}
        generationPrompt={generationPrompt}
        userInput={userInput}
        generationModel={generationModel}
        modelDisplayName={modelDisplayName}
        templateCategory={templateCategory}
        variantSlug={variantSlug}
        t={t}
      />
    );
  }

  if (isResult) {
    return (
      <ResultCard
        onRestart={onRestart}
        onSignUpClick={onSignUpClick}
        templateCategory={templateCategory}
        variantSlug={variantSlug}
        t={t}
      />
    );
  }

  if (config.inputType === "file") {
    return (
      <FileInputCard
        config={config}
        step={step}
        error={error}
        generationPrompt={generationPrompt}
        generationModel={generationModel}
        modelDisplayName={modelDisplayName}
        selectedFile={selectedFile}
        inputPreviewUrl={inputPreviewUrl}
        isProcessing={isProcessing}
        onFileSelected={onFileSelected}
        onSubmit={onSubmit}
        onRemoveFile={onRemoveFile}
        t={t}
      />
    );
  }

  return (
    <TextInputCard
      error={error}
      generationModel={generationModel}
      modelDisplayName={modelDisplayName}
      isProcessing={isProcessing}
      onTextSubmit={onTextSubmit}
      t={t}
    />
  );
}

function FileInputCard({
  config,
  step,
  error,
  generationPrompt,
  generationModel,
  modelDisplayName,
  selectedFile,
  inputPreviewUrl,
  isProcessing,
  onFileSelected,
  onSubmit,
  onRemoveFile,
  t,
}: {
  config: WorkflowInputConfig;
  step: ContentWorkflowStep;
  error: string | null;
  generationPrompt: string | null;
  generationModel: GenerationModel;
  modelDisplayName: string;
  selectedFile: File | null;
  inputPreviewUrl: string | null;
  isProcessing: boolean;
  onFileSelected: (file: File) => void;
  onSubmit: (userInput?: string) => void;
  onRemoveFile: () => void;
  t: ReturnType<typeof useTranslations<"ClientKeys.creative_templates">>;
}) {
  const isFileSelected = step === "file_selected";
  const inputIsVideo = config.acceptedMimeTypes.startsWith("video/") || config.acceptedMimeTypes === "video/*";
  // When the CMS prompt doesn't contain {user_input}, display it as read-only text
  const isPromptEditable = generationPrompt?.includes(USER_INPUT_PLACEHOLDER) ?? false;
  const displayPrompt = generationPrompt && !isPromptEditable ? generationPrompt : undefined;
  const hasPrompt = !!generationPrompt;

  const [editableText, setEditableText] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const textRef = useRef<HTMLTextAreaElement | null>(null);

  const handleEditableTextChange = useCallback(
    (value: string) => {
      setEditableText(value);
      if (validationError) setValidationError(null);
    },
    [validationError],
  );

  const handleSubmit = useCallback(() => {
    if (isPromptEditable) {
      if (!editableText.trim()) {
        setValidationError(t("error_text_required"));
        textRef.current?.focus();
        return;
      }
      onSubmit(editableText.trim());
    } else {
      onSubmit();
    }
  }, [isPromptEditable, editableText, onSubmit, t]);

  const modelBadge = <ModelBadge generationModel={generationModel} modelDisplayName={modelDisplayName} />;

  // Build prompt props once — shared between file-selected and dropzone views
  const promptProps = isPromptEditable
    ? {
        text: editableText,
        onTextChange: isProcessing ? undefined : handleEditableTextChange,
        textPlaceholder: t("omnibox_subtitle_text"),
        textDisabled: isProcessing,
        textLoading: isProcessing,
        textRef,
      }
    : hasPrompt
      ? {
          text: displayPrompt,
          textPlaceholder: t("omnibox_subtitle_text"),
          textLoading: isProcessing,
        }
      : {};

  if (selectedFile && (isProcessing || isFileSelected)) {
    let statusLabel: string | undefined;
    if (step === "uploading") statusLabel = t("uploading");
    else if (step === "generating") statusLabel = t("generating");

    return (
      <PromptIsland
        files={[fileToAttachment(selectedFile, inputPreviewUrl, { isVideo: inputIsVideo, statusLabel })]}
        onRemoveFile={isProcessing ? undefined : () => onRemoveFile()}
        {...promptProps}
        toolbarLeft={modelBadge}
        onSubmit={isFileSelected ? handleSubmit : undefined}
        submitDisabled={!isFileSelected}
        submitLoading={isProcessing}
        error={validationError || error}
      />
    );
  }

  return (
    <PromptIsland
      acceptedMimeTypes={config.acceptedMimeTypes}
      onFilesDrop={files => {
        if (files[0]) onFileSelected(files[0]);
        if (isPromptEditable) textRef.current?.focus();
      }}
      dropzoneLabel={inputIsVideo ? t("omnibox_dropzone_video") : t("omnibox_dropzone_image")}
      dropzoneIcon={inputIsVideo ? <IconVideoClip className={`${iconStrokeFix} tw-size-[18px]`} /> : undefined}
      {...promptProps}
      toolbarLeft={modelBadge}
      error={error}
    />
  );
}

function TextInputCard({
  error,
  generationModel,
  modelDisplayName,
  isProcessing,
  onTextSubmit,
  t,
}: {
  error: string | null;
  generationModel: GenerationModel;
  modelDisplayName: string;
  isProcessing: boolean;
  onTextSubmit: (text: string) => void;
  t: ReturnType<typeof useTranslations<"ClientKeys.creative_templates">>;
}) {
  const [text, setText] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const textRef = useRef<HTMLTextAreaElement | null>(null);

  const handleTextChange = useCallback(
    (value: string) => {
      setText(value);
      if (validationError) setValidationError(null);
    },
    [validationError],
  );

  const handleSubmit = useCallback(() => {
    if (!text.trim()) {
      setValidationError(t("error_text_required"));
      textRef.current?.focus();
      return;
    }
    onTextSubmit(text);
  }, [text, onTextSubmit, t]);

  const modelBadge = <ModelBadge generationModel={generationModel} modelDisplayName={modelDisplayName} />;

  return (
    <PromptIsland
      text={text}
      onTextChange={isProcessing ? undefined : handleTextChange}
      textPlaceholder={t("omnibox_subtitle_text")}
      textRef={textRef}
      textDisabled={isProcessing}
      textLoading={isProcessing}
      toolbarLeft={modelBadge}
      onSubmit={!isProcessing ? handleSubmit : undefined}
      submitLoading={isProcessing}
      submitLabel={t("generate_button")}
      error={validationError || error}
    />
  );
}

function RateLimitedCard({
  redirectCtaUrl,
  onRestart,
  isVideo = false,
  selectedFile,
  inputPreviewUrl,
  generationPrompt,
  userInput,
  generationModel,
  modelDisplayName,
  templateCategory,
  variantSlug,
  t,
}: {
  redirectCtaUrl: string | null;
  onRestart: () => void;
  isVideo?: boolean;
  selectedFile: File | null;
  inputPreviewUrl: string | null;
  generationPrompt: string | null;
  userInput: string | null;
  generationModel: GenerationModel;
  modelDisplayName: string;
  templateCategory: string;
  variantSlug: string;
  t: ReturnType<typeof useTranslations<"ClientKeys.creative_templates">>;
}) {
  const [showDescription, setShowDescription] = useState(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setShowDescription(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const buttonHref = redirectCtaUrl ?? publicUrls.signUp;
  const description = t(isVideo ? "rate_limit_description_video" : "rate_limit_description");
  const buttonText = t(isVideo ? "rate_limit_button_video" : "rate_limit_button");
  const inputIsVideo = selectedFile?.type.startsWith("video/") ?? false;

  return (
    <PromptIsland
      files={selectedFile ? [fileToAttachment(selectedFile, inputPreviewUrl, { isVideo: inputIsVideo })] : undefined}
      onRemoveFile={selectedFile ? () => onRestart() : undefined}
      textPlaceholder={
        generationPrompt?.replaceAll(USER_INPUT_PLACEHOLDER, userInput ?? "") ?? t("omnibox_subtitle_text")
      }
      textAnnotation={
        <p
          className={`tw-px-3 tw-py-2 tw-font-body tw-text-base tw-leading-normal tw-tracking-[0.01em] tw-text-black tw-transition-opacity tw-duration-500 tw-ease-out ${showDescription ? "tw-opacity-100" : "tw-opacity-0"}`}
        >
          {description}
        </p>
      }
      toolbarLeft={<ModelBadge generationModel={generationModel} modelDisplayName={modelDisplayName} />}
      toolbarRight={
        <LinkTracked
          href={buttonHref}
          asButton
          buttonVariants={{ variant: "primary", size: "md" }}
          eventName={EventName.mw_creative_template_omnibox_limit_sign_up_clicked}
          eventProperties={{ template_category: templateCategory, variant_slug: variantSlug }}
        >
          {buttonText}
        </LinkTracked>
      }
    />
  );
}

function ResultCard({
  onRestart,
  onSignUpClick,
  templateCategory,
  variantSlug,
  t,
}: {
  onRestart: () => void;
  onSignUpClick: () => void;
  templateCategory: string;
  variantSlug: string;
  t: ReturnType<typeof useTranslations<"ClientKeys.creative_templates">>;
}) {
  return (
    <PromptIsland toolbarLeft={null}>
      <div className='tw-flex tw-flex-1 tw-flex-col tw-p-3'>
        <div className='tw-flex tw-flex-col tw-gap-3.5'>
          <p className='tw-type-2xl tw-text-black tw-tracking-[-0.2px]'>{t("quota_title")}</p>
          <p className='tw-type-base tw-text-black tw-opacity-60 tw-tracking-[0.16px]'>{t("quota_description")}</p>
        </div>
      </div>
      <div className='tw-mt-auto tw-flex tw-items-center tw-justify-between'>
        <Button
          variant='primary'
          size='md'
          onClick={() => {
            captureEvent(
              EventName.mw_creative_template_omnibox_limit_sign_up_clicked,
              { template_category: templateCategory, variant_slug: variantSlug },
              {
                transport: "sendBeacon",
              },
            );
            onSignUpClick();
          }}
        >
          {t("quota_button")}
        </Button>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type='button'
                onClick={onRestart}
                aria-label={t("try_another")}
                className='tw-flex tw-size-11 tw-items-center tw-justify-center tw-rounded-full tw-bg-white tw-outline-none focus-visible:tw-outline-focus tw-shadow-[0px_0px_0px_1px_rgba(0,0,0,0.06),0px_1px_2px_0px_rgba(0,0,0,0.04),0px_2px_4px_0px_rgba(0,0,0,0.04)]'
              >
                <IconCrossLarge className={`${iconStrokeFix} tw-size-5 tw-text-black`} />
              </button>
            </TooltipTrigger>
            <TooltipContent variant='dark-new'>{t("try_another")}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </PromptIsland>
  );
}
